import { useMemo, useState } from 'react';

import classNames from 'classnames';
import { get } from 'lodash';

import { Loader } from '@/components/global/Loader';
import { AmbassadorProfile } from '@/models/authModels';
import { CampaignStats } from '@/models/campaignModels';
import { PortalDashboardCustomization } from '@/models/customizationModels';
import { ServerStatus, StatItemType } from '@/models/globalModels';
import { selectActiveDashboardCustomization } from '@/redux/customizations/selectors';
import { useAppSelector } from '@/redux/hooks';

import styles from './index.module.scss';
import StatItem from './StatItem';

type StatsListProps = { items: StatItemType[]; applyDashboardFlags?: boolean };

const SHOW_FLAG_BY_TITLE_KEY: Record<string, keyof PortalDashboardCustomization | undefined> = {
  points_earned_title: 'show_points_earned',
  points_available_title: 'show_points_available',
  points_redeemed_title: 'show_points_redeemed',
  points_expired_title: 'show_points_expired',
  cash_available_title: 'show_cash_available',
  cash_redeemed_title: 'show_cash_redeemed',
  cash_expired_title: 'show_cash_expired',
  cash_earned_title: 'show_cash_earned',
  earned_title: 'show_earned',
  awaiting_approval_title: 'show_awaiting_approval',
  awaiting_payment_title: 'show_awaiting_payment',
  paid_out_so_far_title: 'show_paid_out_so_far',
  rewards_cash_earned_title: 'show_rewards_cash_earned',
  rewards_cash_fulfilled_title: 'show_rewards_cash_fulfilled',
  rewards_cash_balance_title: 'show_rewards_cash_balance',
  rewards_points_fulfilled_title: 'show_rewards_points_fulfilled',
  rewards_points_earned_title: 'show_rewards_points_earned',
  rewards_points_balance_title: 'show_rewards_points_balance',
  show_referral_earned_first_event_text: 'show_referral_earned_first_event',
  show_referral_earned_second_event_text: 'show_referral_earned_second_event',
  rewards_cash_redeemed_title: 'show_rewards_cash_redeemed',
  rewards_cash_expired_title: 'show_rewards_cash_expired',
  rewards_points_redeemed_title: 'show_rewards_points_redeemed',
  rewards_points_expired_title: 'show_rewards_points_expired',
};

const StatsList = ({ items, applyDashboardFlags = true }: StatsListProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const currency = useAppSelector((s) => s.auth.companyAccount?.currency);
  const ambassadorProfile = useAppSelector((s) => s.auth.ambassadorProfile);
  const ambassadorStats = useAppSelector((s) => s.campaigns.ambassadorStats);
  const statsStatus = useAppSelector((s) => s.campaigns.statsStatus);

  const dashboard = useAppSelector(selectActiveDashboardCustomization) || ({} as Partial<PortalDashboardCustomization>);

  const stats = useMemo(() => {
    if (!items || !ambassadorProfile) return [];

    const visible = !applyDashboardFlags
      ? items
      : items.filter((stat) => {
          if (!stat.titleKey) return true;
          const flagKey = SHOW_FLAG_BY_TITLE_KEY[stat.titleKey];
          if (!flagKey) return true;
          const flagVal = (dashboard as any)[flagKey];
          return typeof flagVal === 'boolean' ? flagVal : true;
        });

    return visible.map((stat) => {
      const nameOverride = stat.titleKey && (dashboard as any)[stat.titleKey] ? String((dashboard as any)[stat.titleKey]) : stat.name;

      if (stat.dataFromStatsEnpoint) {
        return {
          ...stat,
          name: nameOverride,
          money: stat.moneyKey ? (get(ambassadorStats, stat.moneyKey, 0) as number) : undefined,
          points: stat.pointsKey ? (get(ambassadorStats, stat.pointsKey, 0) as number) : undefined,
        };
      }

      return {
        ...stat,
        name: nameOverride,
        money: stat.moneyKey ? ((ambassadorProfile[stat.moneyKey as keyof AmbassadorProfile] as number) ?? 0) : undefined,
        points: stat.pointsKey ? ((ambassadorProfile[stat.pointsKey as keyof AmbassadorProfile] as number) ?? 0) : undefined,
      };
    });
  }, [items, ambassadorProfile, ambassadorStats, dashboard, applyDashboardFlags]);

  if (statsStatus === ServerStatus.FETCHING) {
    return (
      <div className="h-100">
        <Loader size={30} strokeSize={3} />
      </div>
    );
  }

  if (stats.length === 0) return null;

  return (
    <div className={classNames(styles.wrapper, stats.length > 2 && styles.isCollapsable)}>
      <div className={styles.container}>
        {stats.slice(0, stats.length > 2 && !isCollapsed ? 2 : undefined).map((stat) => (
          <StatItem key={`${stat.type}-${stat.variant}`} {...stat} currency={currency} />
        ))}
      </div>

      {stats.length > 2 && (
        <span
          className={classNames('icon-arrow-down-dark ms-auto me-2 mt-1 f-10', styles.collapseIcon, !isCollapsed && styles.isCollapsed)}
          onClick={() => setIsCollapsed((prev) => !prev)}
        />
      )}
    </div>
  );
};

export default StatsList;
